import ImprivataLogo from '@assets/Imprivata_Logo.svg?react';
import { useSession } from '@common/authorization/context/Session';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { AppError } from '@errorHandler/errors';
import { Button, Modal } from '@imprivata-cloud/components';
import { Consent } from '@imprivata-cloud/consent';
import { useBiometricConsentPorts } from '@selfservice/hooks/useBiometricConsentPorts';
import { useEnrollFace } from '@selfservice/hooks/useEnrollFace';
import { useSelfServiceData } from '@selfservice/utils/useSelfServiceData';
import { Flex, Spin } from 'antd';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { RootRoutes } from '@/index';
import { useNavigateKeepSearch } from '@hooks/useNavigateKeepSearch';
import { CONSENT_DOCUMENT_UNAVAILABLE_MESSAGE, isFaceEnabled } from '@/self-service/utils/helpers';
import { EnrollmentCompletedCard } from '../EnrollFace/EnrollmentCompletedCard/EnrollmentCompletedCard';
import '../../styles.less';
import './FaceCapture.less';
import { EnrollFlow } from '../EnrollFace/EnrollFlow/EnrollFlow';

const FaceCapture: React.FC = () => {
  const { t } = useTranslation();

  const { userInfo } = useSession();
  const { data, isLoading, isError, error } = useSelfServiceData();
  const { emitError } = useErrorContext();

  const navigateKeepSearch = useNavigateKeepSearch();

  const {
    onConsentComplete,
    onSuccessfulFaceEnrollment,
    onCancel,
    onEnrollmentDone,
    isModalOpen,
    screenState,
  } = useEnrollFace();

  const { input: consentInput, ports: consentPorts } = useBiometricConsentPorts(
    userInfo,
    data?.residenceLocations,
  );

  useEffect(() => {
    // Wait for both data and camera check to complete before redirecting
    if (!isLoading && !isFaceEnabled(data)) {
      navigateKeepSearch(RootRoutes.SELF_SERVICE);
    }
  }, [data, isLoading, navigateKeepSearch]);

  if (isError) {
    if (error instanceof AppError) {
      emitError(error);
    } else {
      emitError(AppError.fromMessage('Error getting self service data'));
    }
  }

  const onConsentUnavailable = useCallback(() => {
    emitError(AppError.fromMessage(CONSENT_DOCUMENT_UNAVAILABLE_MESSAGE));
    onCancel();
  }, [emitError, onCancel]);

  const renderContent = () => {
    if (screenState === 'welcome') {
      return (
        <Consent
          input={consentInput}
          ports={consentPorts}
          translate={(key, fallback, params) => t(key, { defaultValue: fallback, ...params })}
          onAbort={onConsentUnavailable}
          onComplete={onConsentComplete}
          onCancel={onCancel}
          onError={message => emitError(AppError.fromMessage(message))}
        />
      );
    }

    if (screenState === 'face-enrolled') {
      return <EnrollmentCompletedCard onDone={onEnrollmentDone} />;
    }

    return null;
  };

  if (isLoading) {
    return (
      <div className="enroll-content">
        <Flex align="center" justify="center">
          <Spin />
        </Flex>
      </div>
    );
  }

  return (
    <div className="enroll-content">
      <Modal
        open={isModalOpen && screenState === 'enrollment'}
        onCancel={onCancel}
        closeIcon={false}
        className="face-enroll-modal"
        destroyOnHidden
        wrapClassName="enroll-modal-wrapper"
        classNames={{ mask: 'enroll-modal-mask' }}
        footer={
          <div className="enroll-footer">
            <div className="enroll-cancel-button">
              <Button aria-label="enroll-cancel-button" type="link" onClick={onCancel}>
                {t('common.cancel')}
              </Button>
            </div>
            <div className="enroll-footer-logo">
              <ImprivataLogo />
            </div>
          </div>
        }
        maskClosable={false}
      >
        <div className="enroll-container">
          <EnrollFlow
            userInfo={userInfo}
            data={data}
            onSuccessfulFaceEnrollment={onSuccessfulFaceEnrollment}
          />
        </div>
      </Modal>

      {renderContent()}
    </div>
  );
};

export default FaceCapture;
